import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client, { apiErr } from "../lib/api";
import { Spinner, StatCard } from "../components/ui-kit";
import { UserPlus, Power, Settings2, Contact, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { money } from "../components/team/shared";
import { MemberForm } from "../components/partners/MemberForm";
import { CommissionUpload } from "../components/partners/CommissionUpload";
import { AssignLeads } from "../components/partners/AssignLeads";
import { LeadStats } from "../components/partners/LeadStats";
import { LeadDistribution } from "../components/partners/LeadDistribution";
import MarketingStudio from "../components/team/MarketingStudio";

function MemberCard({ m, onEdit, onToggle }) {
  return (
    <div className="surface surface-hover p-4 transition-colors duration-200" data-testid={`partner-${m.id}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gold text-black flex items-center justify-center font-head font-bold">{m.name[0]}</div>
        <div className="min-w-0"><div className="text-white truncate">{m.name}</div><div className="text-xs text-slate-500 truncate">{m.email}</div></div>
        <span className={`ml-auto w-2 h-2 rounded-full ${m.active ? "bg-bull" : "bg-bear"}`} />
      </div>
      <div className="grid grid-cols-4 gap-2 mt-4 text-center font-mono text-xs">
        {[["Clients", m.stats.clients], ["Funding", money(m.stats.funding)], ["Earned", money(m.stats.earnings)], ["Share", `${m.share_pct}%`]].map(([l, v]) => (
          <div key={l}><div className="text-sm text-white truncate">{v}</div><div className="text-slate-500 uppercase text-[10px]">{l}</div></div>
        ))}
      </div>
      <div className="text-[11px] text-slate-500 mt-3">{m.tools.length} tools · {m.stats.leads} leads · {m.telegram_connected ? <span className="text-bull">Telegram ✓</span> : "Telegram not connected"}</div>
      <div className="flex gap-2 mt-3">
        <button data-testid={`edit-partner-${m.id}`} onClick={onEdit} className="flex-1 py-1.5 text-xs border border-[color:var(--border-strong)] text-slate-300 hover:text-white flex items-center justify-center gap-1.5"><Settings2 size={12} />Manage</button>
        <button data-testid={`toggle-partner-${m.id}`} onClick={onToggle} className="flex-1 py-1.5 text-xs border border-[color:var(--border-strong)] text-slate-400 hover:text-white flex items-center justify-center gap-1.5"><Power size={12} />{m.active ? "Disable" : "Enable"}</button>
      </div>
    </div>
  );
}

export default function Partners() {
  const [rows, setRows] = useState(null);
  const [tools, setTools] = useState([]);
  const [edit, setEdit] = useState(null);
  const [assign, setAssign] = useState(false);
  const [studio, setStudio] = useState(false);
  const load = () => client.get("/partners").then(({ data }) => setRows(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => {
    load();
    client.get("/partners/options").then(({ data }) => setTools(data.tools)).catch(() => {});
  }, []);
  const toggle = async (m) => {
    try { await client.patch(`/partners/${m.id}`, { active: !m.active }); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const tot = (k) => (rows || []).reduce((a, m) => a + (m.stats[k] || 0), 0);
  return (
    <Layout title="Team Partners">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard testid="pt-members" label="Partners" value={rows ? rows.length : "—"} />
        <StatCard testid="pt-clients" label="Partner Clients" value={tot("clients")} />
        <StatCard testid="pt-funding" label="Total Funding" value={money(tot("funding"))} accent="gold" />
        <StatCard testid="pt-payout" label="Total Payout" value={money(tot("earnings"))} accent="bull" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button data-testid="studio-toggle-btn" onClick={() => setStudio((s) => !s)} className="px-3 py-1.5 text-xs border border-gold text-gold hover:bg-[color:var(--gold)] hover:text-black flex items-center gap-1.5 transition-colors duration-200"><Sparkles size={14} />{studio ? "Hide" : "Open"} Marketing Studio</button>
        <button data-testid="assign-leads-btn" disabled={!rows?.length} onClick={() => setAssign(true)} className="ml-auto px-3 py-1.5 text-xs border border-[color:var(--border-strong)] text-slate-300 hover:text-white flex items-center gap-1.5 disabled:opacity-50"><Contact size={14} />Assign Leads</button>
        <button data-testid="add-partner-btn" onClick={() => setEdit({})} className="btn-gold px-3 py-1.5 text-xs flex items-center gap-1.5"><UserPlus size={14} />Add Partner</button>
      </div>
      {studio && <div className="mb-5"><MarketingStudio limit={1000} /></div>}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_440px] gap-5">
        {!rows ? <Spinner /> : rows.length === 0 ? <div className="surface p-10 text-center text-sm text-slate-500" data-testid="partners-empty">Abhi koi team partner nahi hai. "Add Partner" se pehla member banayein.</div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 content-start">
            {rows.map((m) => <MemberCard key={m.id} m={m} onEdit={() => setEdit(m)} onToggle={() => toggle(m)} />)}
          </div>
        )}
        <CommissionUpload onDone={load} members={rows || []} />
      </div>
      {rows && rows.length > 0 && <div className="mt-5"><LeadDistribution members={rows} onChange={load} /></div>}
      {rows && rows.length > 0 && <div className="mt-5"><LeadStats refreshKey={rows} /></div>}
      {edit && <MemberForm member={edit.id ? edit : null} tools={tools} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} onChange={load} />}
      {assign && <AssignLeads members={rows} onClose={() => setAssign(false)} onDone={() => { setAssign(false); load(); }} />}
    </Layout>
  );
}
